import React, { useState, useEffect } from 'react';
import { 
  School, Plus, Edit, Trash2, Check, X, ChevronDown, ChevronUp,
  BarChart2, Building, Mail, Palette, Loader, AlertTriangle, 
  Lock, Unlock, Users, List
} from 'lucide-react';
import toast from 'react-hot-toast';

const SchoolsPage = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddSchoolModalOpen, setIsAddSchoolModalOpen] = useState(false);
  const [expandedSchoolId, setExpandedSchoolId] = useState(null);
  const [isEditingSchool, setIsEditingSchool] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  
  // New state for department form
  const [isAddDepartmentModalOpen, setIsAddDepartmentModalOpen] = useState(false);
  const [selectedSchoolForDepartment, setSelectedSchoolForDepartment] = useState(null);
  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    code: ''
  });

  // New state for waitlist modals
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [waitlistCount, setWaitlistCount] = useState({});
  const [isInviteWaitlistModalOpen, setIsInviteWaitlistModalOpen] = useState(false);
  const [waitlistInvitationProcessing, setWaitlistInvitationProcessing] = useState(false);

  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState(null);
  
  // Department deletion states
  const [deleteDepartmentConfirmation, setDeleteDepartmentConfirmation] = useState(null);
  const [isDeletingDepartment, setIsDeletingDepartment] = useState(false);

  // Form state for adding/editing schools
  const [schoolForm, setSchoolForm] = useState({
    name: '',
    domain: '',
    logoUrl: '',
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
    isSignupPaused: false,
  });

  useEffect(() => {
    fetchSchools();
    fetchWaitlistCounts();
  }, []);

  const fetchSchools = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch schools');
      }

      const data = await response.json();
      setSchools(data);
    } catch (error) {
      console.error('Error fetching schools:', error);
      toast.error('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const fetchWaitlistCounts = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/waitlist/counts`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch waitlist counts');
      }

      const data = await response.json();
      setWaitlistCount(data.counts);
    } catch (error) {
      console.error('Error fetching waitlist counts:', error);
    }
  };

  const fetchSchoolDetails = async (schoolId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch school details');
      }

      const data = await response.json();
      
      // Update the school in the list with detailed information
      setSchools(schools.map(school => 
        school.id === schoolId ? { ...school, departments: data.departments } : school
      ));
    } catch (error) {
      console.error('Error fetching school details:', error);
      toast.error('Failed to load school details');
    }
  };

  const handleToggleProfessorRatings = async (schoolId, currentStatus) => {
    try {
      console.log(`Toggling professor ratings for school ${schoolId}: current status is ${currentStatus ? 'enabled' : 'disabled'}`);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/schools/${schoolId}/professor-ratings`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ enabled: !currentStatus })
        }
      );
  
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle professor ratings');
      }
  
      const updatedData = await response.json();
      console.log('Toggle professor ratings response:', updatedData);
      
      // Update school in state
      setSchools(schools.map(school => 
        school.id === schoolId ? { ...school, professorRatingsEnabled: !currentStatus } : school
      ));
      
      toast.success(
        !currentStatus 
          ? 'Professor ratings enabled for this school' 
          : 'Professor ratings disabled for this school'
      );
      
    } catch (error) {
      console.error('Error toggling professor ratings:', error);
      toast.error(error.message || 'Failed to toggle professor ratings status');
    }
  };

  const handleToggleSignupPause = async (schoolId, currentStatus) => {
    try {
      console.log(`Toggling signup status for school ${schoolId}: current status is ${currentStatus ? 'paused' : 'active'}`);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/schools/${schoolId}/toggle-signup`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isSignupPaused: !currentStatus })
        }
      );
  
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle signup status');
      }
  
      const updatedData = await response.json();
      console.log('Toggle signup response:', updatedData);
      
      // Update school in state
      setSchools(schools.map(school => 
        school.id === schoolId ? { ...school, isSignupPaused: Boolean(updatedData.isSignupPaused) } : school
      ));
      
      toast.success(
        updatedData.isSignupPaused 
          ? 'Signups paused for this school' 
          : 'Signups enabled for this school'
      );
      
    } catch (error) {
      console.error('Error toggling signup status:', error);
      toast.error(error.message || 'Failed to update signup status');
    }
  };

  const handleInviteWaitlist = async (schoolId) => {
    try {
      setWaitlistInvitationProcessing(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/waitlist/${schoolId}/invite`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send waitlist invitations');
      }

      const result = await response.json();
      
      // Close modal
      setIsInviteWaitlistModalOpen(false);
      
      // Update waitlist counts
      fetchWaitlistCounts();
      
      toast.success(`Sent ${result.invitedCount} waitlist invitations successfully`);
    } catch (error) {
      console.error('Error sending waitlist invitations:', error);
      toast.error(error.message || 'Failed to send waitlist invitations');
    } finally {
      setWaitlistInvitationProcessing(false);
    }
  };

  const handleViewWaitlist = async (schoolId) => {
    setSelectedSchoolId(schoolId);
    setIsWaitlistModalOpen(true);
    setWaitlistLoading(true);
    setWaitlistError(null);
    
    try {
      console.log(`Fetching waitlist for school ID: ${schoolId}`);
      
      // Based on the API code analysis, there's a mismatch in the path parameter extraction
      // The route expects /api/admin/waitlist/{schoolId} but the extraction logic gets the wrong index
      // Let's use this format but add a console log to debug any issues
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/waitlist/${schoolId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
  
      console.log(`Waitlist API response status: ${response.status}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }
  
      const data = await response.json();
      console.log(`Successfully fetched waitlist data:`, data);
      setWaitlistEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
      setWaitlistError(error.message);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleInviteWaitlistModal = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setIsInviteWaitlistModalOpen(true);
  };

  const handleAddSchool = async () => {
    try {
      // Validate form
      if (!schoolForm.name || !schoolForm.domain) {
        toast.error('School name and domain are required');
        return;
      }
  
      if (!schoolForm.domain.endsWith('.edu')) {
        toast.error('Domain must end with .edu');
        return;
      }
      
      // Create a complete form data object
      const formData = {
        ...schoolForm,
        isSignupPaused: Boolean(schoolForm.isSignupPaused) // Ensure boolean type
      };
  
      console.log('Submitting school form:', formData);
  
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );
  
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add school');
      }
  
      const newSchool = await response.json();
      
      // Add new school to state
      setSchools([...schools, newSchool]);
      
      // Reset form and close modal
      setSchoolForm({
        name: '',
        domain: '',
        logoUrl: '',
        primaryColor: '#000000',
        secondaryColor: '#FFFFFF',
        isSignupPaused: false,
      });
      setIsAddSchoolModalOpen(false);
      
      toast.success('School added successfully');
    } catch (error) {
      console.error('Error adding school:', error);
      toast.error(error.message || 'Failed to add school');
    }
  };

  const handleEditSchool = async (schoolId) => {
    try {
      // Validate form
      if (!schoolForm.name || !schoolForm.domain) {
        toast.error('School name and domain are required');
        return;
      }
  
      if (!schoolForm.domain.endsWith('.edu')) {
        toast.error('Domain must end with .edu');
        return;
      }
      
      // Create a complete form data object
      const formData = {
        ...schoolForm,
        isSignupPaused: Boolean(schoolForm.isSignupPaused) // Ensure boolean type
      };
  
      console.log('Updating school with data:', formData);
  
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );
  
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update school');
      }
  
      const updatedSchool = await response.json();
      
      // Update school in state
      setSchools(schools.map(school => 
        school.id === schoolId ? { ...school, ...updatedSchool } : school
      ));
      
      // Reset form and exit edit mode
      setIsEditingSchool(null);
      
      toast.success('School updated successfully');
    } catch (error) {
      console.error('Error updating school:', error);
      toast.error(error.message || 'Failed to update school');
    }
  };

  const handleDeleteSchool = async (schoolId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete school');
      }
      
      // Remove school from state
      setSchools(schools.filter(school => school.id !== schoolId));
      
      // Close delete confirmation
      setDeleteConfirmation(null);
      
      toast.success('School deleted successfully');
    } catch (error) {
      console.error('Error deleting school:', error);
      toast.error(error.message || 'Failed to delete school');
    }
  };

  const handleAddDepartment = async (schoolId, departmentData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/departments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...departmentData,
            schoolId
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add department');
      }

      const newDepartment = await response.json();
      
      // Update school departments in state
      setSchools(schools.map(school => {
        if (school.id === schoolId) {
          const updatedDepartments = school.departments ? [...school.departments, newDepartment] : [newDepartment];
          return { ...school, departments: updatedDepartments };
        }
        return school;
      }));
      
      // Close modal and reset form
      setIsAddDepartmentModalOpen(false);
      setDepartmentForm({
        name: '',
        code: ''
      });
      setSelectedSchoolForDepartment(null);
      
      toast.success('Department added successfully');
    } catch (error) {
      console.error('Error adding department:', error);
      toast.error(error.message || 'Failed to add department');
    }
  };

  // New function to handle department deletion
  const handleDeleteDepartment = async (schoolId, departmentId) => {
    try {
      setIsDeletingDepartment(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/departments/${departmentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete department');
      }
      
      // Update school departments in state
      setSchools(schools.map(school => {
        if (school.id === schoolId) {
          return {
            ...school,
            departments: school.departments.filter(dept => dept.id !== departmentId)
          };
        }
        return school;
      }));
      
      // Close confirmation dialog
      setDeleteDepartmentConfirmation(null);
      
      toast.success('Department deleted successfully');
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error(error.message || 'Failed to delete department');
    } finally {
      setIsDeletingDepartment(false);
    }
  };

  // New function to open the department modal for a specific school
  const openAddDepartmentModal = (schoolId) => {
    setSelectedSchoolForDepartment(schoolId);
    setIsAddDepartmentModalOpen(true);
  };

  const toggleSchoolExpansion = (schoolId) => {
    if (expandedSchoolId === schoolId) {
      setExpandedSchoolId(null);
    } else {
      setExpandedSchoolId(schoolId);
      // Fetch detailed information if not already loaded
      const school = schools.find(s => s.id === schoolId);
      if (!school.departments) {
        fetchSchoolDetails(schoolId);
      }
    }
  };

  const startEditingSchool = (school) => {
    setSchoolForm({
      name: school.name,
      domain: school.domain,
      logoUrl: school.logoUrl || '',
      primaryColor: school.primaryColor || '#000000',
      secondaryColor: school.secondaryColor || '#FFFFFF',
      isSignupPaused: Boolean(school.isSignupPaused),
    });
    setIsEditingSchool(school.id);
  };
  

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">School Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage schools and departments on the EDX Red platform
          </p>
        </div>
        <button
          onClick={() => setIsAddSchoolModalOpen(true)}
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          <Plus size={18} className="mr-2" />
          Add School
        </button>
      </div>

      {/* School Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-4">
              <School size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Schools</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {loading ? '...' : schools.length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 mr-4">
              <Mail size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Supported Domains</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {loading ? '...' : schools.length} .edu
              </p>
            </div>
          </div>
        </div>
        
        {/* New waitlist statistic card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 mr-4">
              <Users size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Waitlisted</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {Object.values(waitlistCount).reduce((a, b) => a + b, 0) || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Schools List */}
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      ) : schools.length > 0 ? (
        <div className="space-y-4">
          {schools.map(school => (
            <div key={school.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {isEditingSchool === school.id ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            School Name
                          </label>
                          <input
                            type="text"
                            value={schoolForm.name}
                            onChange={(e) => setSchoolForm({...schoolForm, name: e.target.value})}
                            className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                            placeholder="University name"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Domain (.edu)
                          </label>
                          <input
                            type="text"
                            value={schoolForm.domain}
                            onChange={(e) => setSchoolForm({...schoolForm, domain: e.target.value})}
                            className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                            placeholder="university.edu"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Logo URL (optional)
                          </label>
                          <input
                            type="text"
                            value={schoolForm.logoUrl}
                            onChange={(e) => setSchoolForm({...schoolForm, logoUrl: e.target.value})}
                            className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                            placeholder="https://example.com/logo.png"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Primary Color
                            </label>
                            <div className="flex">
                              <input
                                type="color"
                                value={schoolForm.primaryColor}
                                onChange={(e) => setSchoolForm({...schoolForm, primaryColor: e.target.value})}
                                className="h-10 w-10 rounded border border-gray-300 dark:border-gray-600"
                              />
                              <input
                                type="text"
                                value={schoolForm.primaryColor}
                                onChange={(e) => setSchoolForm({...schoolForm, primaryColor: e.target.value})}
                                className="ml-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Secondary Color
                            </label>
                            <div className="flex">
                              <input
                                type="color"
                                value={schoolForm.secondaryColor}
                                onChange={(e) => setSchoolForm({...schoolForm, secondaryColor: e.target.value})}
                                className="h-10 w-10 rounded border border-gray-300 dark:border-gray-600"
                              />
                              <input
                                type="text"
                                value={schoolForm.secondaryColor}
                                onChange={(e) => setSchoolForm({...schoolForm, secondaryColor: e.target.value})}
                                className="ml-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3 mt-4">
                          <button
                            onClick={() => setIsEditingSchool(null)}
                            className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleEditSchool(school.id)}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-3">
                          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {school.name}
                          </h2>
                          <div 
                            className="px-2 py-1 text-xs rounded-full" 
                            style={{ 
                              backgroundColor: school.primaryColor || '#000', 
                              color: school.secondaryColor || '#fff' 
                            }}
                          >
                            {school.domain}
                          </div>
                          
                          {/* Signup Status Badge */}
                          <div className={`px-2 py-1 text-xs rounded-full flex items-center ${
                            school.isSignupPaused 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}>
                            {school.isSignupPaused 
                              ? <><Lock size={12} className="mr-1" /> Signups Paused</> 
                              : <><Unlock size={12} className="mr-1" /> Signups Active</>}
                          </div>
                          
                          {/* Professor Ratings Status Badge */}
                          <div className={`px-2 py-1 text-xs rounded-full flex items-center ${
                            school.professorRatingsEnabled 
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                          }`}>
                            {school.professorRatingsEnabled 
                              ? <><BarChart2 size={12} className="mr-1" /> Ratings Enabled</> 
                              : <><X size={12} className="mr-1" /> Ratings Disabled</>}
                          </div>
                          
                          {/* Waitlist Count Badge (only if has waitlisted users) */}
                          {waitlistCount[school.id] > 0 && (
                            <div className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 flex items-center">
                              <Users size={12} className="mr-1" /> 
                              {waitlistCount[school.id]} Waitlisted
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <Palette size={16} className="mr-1" />
                          <div className="flex items-center">
                            <span>Theme: </span>
                            <div 
                              className="w-4 h-4 mx-1 rounded-full border border-gray-300 dark:border-gray-600" 
                              style={{ backgroundColor: school.primaryColor || '#000' }}
                            ></div>
                            <span>/</span>
                            <div 
                              className="w-4 h-4 mx-1 rounded-full border border-gray-300 dark:border-gray-600" 
                              style={{ backgroundColor: school.secondaryColor || '#fff' }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex items-center flex-wrap gap-3">
                          {/* Toggle Signup Button */}
                          <button
                            onClick={() => handleToggleSignupPause(school.id, school.isSignupPaused)}
                            className={`text-sm flex items-center ${
                              school.isSignupPaused
                                ? 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300'
                                : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300'
                            }`}
                          >
                            {school.isSignupPaused 
                              ? <><Unlock size={16} className="mr-1" /> Enable Signups</> 
                              : <><Lock size={16} className="mr-1" /> Pause Signups</>}
                          </button>
                          
                          {/* Toggle Professor Ratings Button */}
                          <button
                            onClick={() => handleToggleProfessorRatings(school.id, school.professorRatingsEnabled)}
                            className={`text-sm flex items-center ${
                              school.professorRatingsEnabled
                                ? 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300'
                                : 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300'
                            }`}
                          >
                            {school.professorRatingsEnabled 
                              ? <><X size={16} className="mr-1" /> Disable Ratings</> 
                              : <><BarChart2 size={16} className="mr-1" /> Enable Ratings</>}
                          </button>
                          
                          {/* Waitlist Management Buttons */}
                          {school.isSignupPaused && (
                            <>
                              <button
                                onClick={() => handleViewWaitlist(school.id)}
                                className="text-sm flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                              >
                                <List size={16} className="mr-1" />
                                View Waitlist
                              </button>
                              
                              {waitlistCount[school.id] > 0 && (
                                <button
                                  onClick={() => handleInviteWaitlistModal(school.id)}
                                  className="text-sm flex items-center text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300"
                                >
                                  <Mail size={16} className="mr-1" />
                                  Send Invitations
                                </button>
                              )}
                            </>
                          )}
                          
                          <button
                            onClick={() => toggleSchoolExpansion(school.id)}
                            className="text-sm flex items-center text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400"
                          >
                            {expandedSchoolId === school.id ? (
                              <>
                                <ChevronUp size={16} className="mr-1" />
                                Hide Departments
                              </>
                            ) : (
                              <>
                                <ChevronDown size={16} className="mr-1" />
                                Show Departments
                              </>
                            )}
                          </button>
                          
                          <button
                            onClick={() => startEditingSchool(school)}
                            className="text-sm flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          >
                            <Edit size={16} className="mr-1" />
                            Edit
                          </button>
                          
                          <button
                            onClick={() => setDeleteConfirmation(school.id)}
                            className="text-sm flex items-center text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                          >
                            <Trash2 size={16} className="mr-1" />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Expanded School Details */}
                {expandedSchoolId === school.id && (
                  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Departments
                      </h3>
                      
                      {/* Added "Add Department" button at the top level */}
                      <button
                        onClick={() => openAddDepartmentModal(school.id)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                      >
                        <Plus size={16} className="mr-1" />
                        Add Department
                      </button>
                    </div>
                    
                    {school.departments ? (
                      school.departments.length > 0 ? (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                            <thead className="bg-gray-100 dark:bg-gray-600">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Code
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Department Name
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                              {school.departments.map(department => (
                                <tr key={department.id} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                    {department.code}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                    {department.name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                      className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-3"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => setDeleteDepartmentConfirmation({ 
                                        schoolId: school.id, 
                                        department 
                                      })}
                                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <Building size={36} className="mx-auto text-gray-400" />
                          <p className="mt-2 text-gray-500 dark:text-gray-400">No departments found</p>
                          <button
                            onClick={() => openAddDepartmentModal(school.id)}
                            className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                          >
                            <Plus size={16} className="mr-1" />
                            Add Department
                          </button>
                        </div>
                      )
                    ) : (
                      <div className="flex justify-center p-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <School size={48} className="mx-auto text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No Schools Found</h3>
          <p className="mt-2 text-gray-500 dark:text-gray-400">
            Add your first school to enable student registration
          </p>
          <button
            onClick={() => setIsAddSchoolModalOpen(true)}
            className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            <Plus size={18} className="mr-2" />
            Add School
          </button>
        </div>
      )}

      {/* Add School Modal */}
      {isAddSchoolModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Add New School
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  School Name
                </label>
                <input
                  type="text"
                  value={schoolForm.name}
                  onChange={(e) => setSchoolForm({...schoolForm, name: e.target.value})}
                  className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                  placeholder="University name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Domain (.edu)
                </label>
                <input
                  type="text"
                  value={schoolForm.domain}
                  onChange={(e) => setSchoolForm({...schoolForm, domain: e.target.value})}
                  className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                  placeholder="university.edu"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Logo URL (optional)
                </label>
                <input
                  type="text"
                  value={schoolForm.logoUrl}
                  onChange={(e) => setSchoolForm({...schoolForm, logoUrl: e.target.value})}
                  className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                  placeholder="https://example.com/logo.png"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Primary Color
                  </label>
                  <div className="flex">
                    <input
                      type="color"
                      value={schoolForm.primaryColor}
                      onChange={(e) => setSchoolForm({...schoolForm, primaryColor: e.target.value})}
                      className="h-10 w-10 rounded border border-gray-300 dark:border-gray-600"
                    />
                    <input
                      type="text"
                      value={schoolForm.primaryColor}
                      onChange={(e) => setSchoolForm({...schoolForm, primaryColor: e.target.value})}
                      className="ml-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Secondary Color
                  </label>
                  <div className="flex">
                    <input
                      type="color"
                      value={schoolForm.secondaryColor}
                      onChange={(e) => setSchoolForm({...schoolForm, secondaryColor: e.target.value})}
                      className="h-10 w-10 rounded border border-gray-300 dark:border-gray-600"
                    />
                    <input
                      type="text"
                      value={schoolForm.secondaryColor}
                      onChange={(e) => setSchoolForm({...schoolForm, secondaryColor: e.target.value})}
                      className="ml-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                    />
                  </div>
                </div>
              </div>
              
              {/* Initial signup status */}
              <div className="flex items-center">
                <input
                  id="paused-signups"
                  type="checkbox"
                  checked={schoolForm.isSignupPaused}
                  onChange={(e) => setSchoolForm({...schoolForm, isSignupPaused: e.target.checked})}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="paused-signups" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Pause signups for this school
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsAddSchoolModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSchool}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Add School
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Department Modal */}
      {isAddDepartmentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Add New Department
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department Name
                </label>
                <input
                  type="text"
                  value={departmentForm.name}
                  onChange={(e) => setDepartmentForm({...departmentForm, name: e.target.value})}
                  className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                  placeholder="Computer Science"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department Code
                </label>
                <input
                  type="text"
                  value={departmentForm.code}
                  onChange={(e) => setDepartmentForm({...departmentForm, code: e.target.value})}
                  className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
                  placeholder="CS"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  This code will be used for course identifiers (e.g., CS101)
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setIsAddDepartmentModalOpen(false);
                  setDepartmentForm({name: '', code: ''});
                  setSelectedSchoolForDepartment(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!departmentForm.name || !departmentForm.code) {
                    toast.error('Department name and code are required');
                    return;
                  }
                  handleAddDepartment(selectedSchoolForDepartment, departmentForm);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Add Department
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Waitlist Modal */}
      {isWaitlistModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Waitlist for {schools.find(s => s.id === selectedSchoolId)?.name}
              </h3>
              <button
                onClick={() => setIsWaitlistModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Waitlist Content */}
            {waitlistLoading ? (
              <div className="flex justify-center p-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
              </div>
            ) : waitlistError ? (
              <div className="p-4 rounded-md bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200">
                <div className="flex items-center">
                  <AlertTriangle size={20} className="mr-2" />
                  <p>Error loading waitlist: {waitlistError}</p>
                </div>
              </div>
            ) : waitlistEntries.length === 0 ? (
              <div className="text-center py-8">
                <Users size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No users on the waitlist</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date Added
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {waitlistEntries.map(entry => (
                      <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {entry.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(entry.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {entry.inviteSent ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                              Invited on {new Date(entry.inviteSentAt).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100">
                              Waiting
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="flex justify-between mt-6">
              <button
                onClick={() => {
                  setIsWaitlistModalOpen(false);
                  handleInviteWaitlistModal(selectedSchoolId);
                }}
                className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                disabled={waitlistLoading || waitlistEntries.filter(entry => !entry.inviteSent).length === 0}
              >
                <Mail size={18} className="mr-2" />
                Send Invitations
              </button>
              <button
                onClick={() => setIsWaitlistModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Waitlist Confirmation Modal */}
      {isInviteWaitlistModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Send Waitlist Invitations
            </h3>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to send email invitations to the {waitlistCount[selectedSchoolId] || 0} waitlisted users for {schools.find(s => s.id === selectedSchoolId)?.name}?
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsInviteWaitlistModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                disabled={waitlistInvitationProcessing}
              >
                Cancel
              </button>
              <button
                onClick={() => handleInviteWaitlist(selectedSchoolId)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                disabled={waitlistInvitationProcessing}
              >
                {waitlistInvitationProcessing ? (
                  <span className="flex items-center">
                    <Loader className="animate-spin h-4 w-4 mr-2" />
                    Sending...
                  </span>
                ) : (
                  'Send Invitations'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Delete School
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this school? This action cannot be undone and will remove all associated departments, courses, and materials.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSchool(deleteConfirmation)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Department Confirmation Modal */}
      {deleteDepartmentConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center text-red-600 dark:text-red-400 mb-4">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-medium">Delete Department</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the department <strong>{deleteDepartmentConfirmation.department.code}: {deleteDepartmentConfirmation.department.name}</strong>? This action cannot be undone and will remove all associated courses and materials.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteDepartmentConfirmation(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                disabled={isDeletingDepartment}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDepartment(
                  deleteDepartmentConfirmation.schoolId,
                  deleteDepartmentConfirmation.department.id
                )}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                disabled={isDeletingDepartment}
              >
                {isDeletingDepartment ? (
                  <span className="flex items-center">
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </span>
                ) : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolsPage;